import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AppShell } from '@/components/layout/AppShell';
import { api, parseErr } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import { useAuth } from '@/lib/auth';
import { useAutoRefetch } from '@/lib/useAutoRefetch';
import { MaskedAmount } from '@/components/common/MaskedAmount';
import { MiniBars } from '@/components/common/MiniBars';
import { StageSOPChips } from '@/components/common/StageSOPChips';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { STAGE_LABEL } from '@/lib/roles';
import { MasterProjectSheetPanel } from '@/components/master/MasterProjectSheetPanel';
import { MilestoneSliderPopover } from '@/components/master/MilestoneSlider';
import { AttachWorkOrderDialog } from '@/components/master/AttachWorkOrderDialog';
import { WoSyncReviewDialog } from '@/components/wo/WoSyncReviewDialog';
import { LinkIcon } from 'lucide-react';
import {
  Database, Plus, Pencil, Trash2, Lock, Info,
  CheckCircle2, XCircle, Search, Download, Upload, FileSpreadsheet, AlertTriangle,
  FileSearch, ChevronDown,
} from 'lucide-react';
import { API_BASE } from '@/lib/api';

const STATUS_LABEL = {
  active: 'Active',
  on_hold: 'On Hold',
  closed: 'Closed',
};
const STATUS_CHIP = {
  active: 'chip-success',
  on_hold: 'chip-warning',
  closed: '',
};

export default function MasterProjectsPage() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [canEdit, setCanEdit] = useState(false);
  const [stages, setStages] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openForm, setOpenForm] = useState(null);   // null | { new: bool, row }
  const [openDelete, setOpenDelete] = useState(null);
  const [attachTarget, setAttachTarget] = useState(null); // project pending WO attach
  const [openImport, setOpenImport] = useState(false);
  const [openWoSync, setOpenWoSync] = useState(false);
  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [linkFilter, setLinkFilter] = useState('all'); // all | linked | unlinked

  const usersById = useMemo(() =>
    Object.fromEntries(users.map(u => [u.id, u])), [users]);

  const load = async () => {
    setLoading(true);
    try {
      const [r, u] = await Promise.all([
        api.get('/master-projects'),
        api.get('/users'),
      ]);
      setRows(r.data.rows || []);
      setCanEdit(!!r.data.can_edit);
      setStages(r.data.stages || []);
      setStatuses(r.data.statuses || []);
      setUsers(u.data || []);
    } catch (e) { toast.error(parseErr(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  useAutoRefetch(load, { collections: ['master_projects', 'users', 'work_orders'] });

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return rows.filter(r => {
      if (statusFilter !== 'all' && r.status !== statusFilter) return false;
      // Iter47 — Link truth is the real MP↔WO connection, NOT the typed number.
      const linked = !!r.is_linked;
      if (linkFilter === 'linked' && !linked) return false;
      if (linkFilter === 'unlinked' && linked) return false;
      if (!needle) return true;
      const displayed = r.wo_number_display || r.work_order_number || r.linked_wo_number || '';
      return (
        (r.project_name || '').toLowerCase().includes(needle) ||
        (r.client || '').toLowerCase().includes(needle) ||
        displayed.toLowerCase().includes(needle)
      );
    });
  }, [rows, q, statusFilter, linkFilter]);

  const unlinkedCount = useMemo(() => rows.filter(r => !r.is_linked).length, [rows]);

  const onDelete = async () => {
    if (!openDelete) return;
    try {
      await api.delete(`/master-projects/${openDelete.id}`);
      toast.success('Deleted');
      setOpenDelete(null);
      load();
    } catch (e) { toast.error(parseErr(e)); }
  };

  const openNew = () => setOpenForm({ new: true, row: {
    project_name: '', client: '', work_order_number: '', work_order_date: '',
    project_stage: stages[0] || 'strategy_concept',
    tenure_months: '', total_amount: '', paid_amount: '', unpaid_amount: '',
    milestones: [], team_member_ids: [],
    creative_lead_id: '', technical_lead_id: '',
    status: 'active', notes: '',
  }});

  const downloadTemplate = async () => {
    try {
      const token = localStorage.getItem('32d.os.token');
      const res = await fetch(`${API_BASE}/master-projects/import/template.xlsx`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const d = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      a.download = `32december_master_projects_template_${d}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success('Template downloaded');
    } catch (e) { toast.error(e.message || 'Download failed'); }
  };

  return (
    <AppShell breadcrumbs={[{ label: 'Master Project Data' }]}>
      <div className="p-6 max-w-[1400px] mx-auto space-y-4" data-testid="master-projects-page">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h1 className="font-heading text-2xl font-semibold flex items-center gap-2">
              <Database size={20} />Master Project Data
            </h1>
            <p className="text-[hsl(var(--foreground-muted))] text-[13px] mt-1">
              Canonical project list. {canEdit
                ? 'Owner & Design Leads can edit; other roles view.'
                : 'You can view — Owner and Design Leads maintain this data.'}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {canEdit && (
              <>
                <Button size="sm" variant="outline" onClick={downloadTemplate} data-testid="mp-download-template">
                  <Download size={13} className="mr-1" />Download Template
                </Button>
                <Button size="sm" variant="outline" onClick={() => setOpenImport(true)} data-testid="mp-import-open">
                  <Upload size={13} className="mr-1" />Import Data
                </Button>
                {user?.role === 'owner' && (
                  <Button size="sm" variant="outline"
                          onClick={() => setOpenWoSync(true)}
                          data-testid="mp-wo-sync-open"
                          title="Review Master Projects whose displayed WO number is empty or conflicts with the connected Work Order.">
                    <LinkIcon size={13} className="mr-1" />Review WO sync
                  </Button>
                )}
                <Button size="sm" onClick={openNew} data-testid="mp-new">
                  <Plus size={14} className="mr-1" />New project
                </Button>
              </>
            )}
          </div>
        </div>

        <MasterProjectSheetPanel onDataChanged={load} />

        <MPVisualSummary rows={rows} />

        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative flex-1 min-w-[200px] max-w-[420px]">
            <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[hsl(var(--foreground-subtle))]" />
            <Input value={q} onChange={e => setQ(e.target.value)}
                   placeholder="Search by name, client, or WO number"
                   className="pl-8 h-9 text-[13px]"
                   data-testid="mp-search" />
          </div>
          <div className="flex items-center gap-1">
            {[{ v: 'all', l: 'All' }, ...statuses.map(s => ({ v: s, l: STATUS_LABEL[s] }))].map(o => (
              <button key={o.v}
                      onClick={() => setStatusFilter(o.v)}
                      className={`h-8 px-3 rounded-md text-[12px] border ${statusFilter === o.v ? 'bg-[hsl(var(--surface-2))] font-semibold' : ''}`}
                      style={{ borderColor: 'hsl(var(--border))' }}
                      data-testid={`mp-filter-${o.v}`}>
                {o.l}
              </button>
            ))}
          </div>
          {unlinkedCount > 0 && (
            <div className="flex items-center gap-1 ml-1" data-testid="mp-link-filters">
              {[
                { v: 'all', l: 'Any WO' },
                { v: 'linked', l: 'WO-linked' },
                { v: 'unlinked', l: `Unlinked · ${unlinkedCount}` },
              ].map(o => (
                <button key={o.v}
                        onClick={() => setLinkFilter(o.v)}
                        className={`h-8 px-3 rounded-md text-[12px] border ${linkFilter === o.v
                            ? (o.v === 'unlinked' ? 'bg-[hsl(48_96%_88%)] border-[hsl(48_80%_65%)] font-semibold text-[hsl(38_60%_25%)]' : 'bg-[hsl(var(--surface-2))] font-semibold')
                            : ''}`}
                        style={{ borderColor: linkFilter === o.v && o.v === 'unlinked' ? 'hsl(48 80% 65%)' : 'hsl(var(--border))' }}
                        data-testid={`mp-link-filter-${o.v}`}>
                  {o.l}
                </button>
              ))}
            </div>
          )}
          <div className="text-[12px] text-[hsl(var(--foreground-muted))] ml-auto">
            {filtered.length} of {rows.length} project{rows.length === 1 ? '' : 's'}
          </div>
        </div>

        {loading ? (
          <div className="p-8 text-center text-[hsl(var(--foreground-muted))]">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="border rounded-lg p-10 text-center text-[hsl(var(--foreground-muted))]"
               style={{ borderColor: 'hsl(var(--border))' }}
               data-testid="mp-empty">
            No projects match. {canEdit && 'Add your first project or connect the master Google Sheet.'}
          </div>
        ) : (
          <div className="border rounded-xl bg-[hsl(var(--card))] overflow-x-auto" style={{ borderColor: 'hsl(var(--border))' }}>
            <table className="tbl">
              <thead>
                <tr>
                  <th>Project</th>
                  <th>Client</th>
                  <th>Stage</th>
                  <th>Status</th>
                  <th>WO</th>
                  <th>Tenure</th>
                  <th>Total</th>
                  <th>Paid</th>
                  <th>Unpaid</th>
                  <th>Milestones</th>
                  <th>Leads / Team</th>
                  {canEdit && <th></th>}
                </tr>
              </thead>
              <tbody>
                {filtered.map(r => {
                  // Iter47 — canonical Link truth from real WO connection.
                  const unlinked = !r.is_linked;
                  const displayNo = r.wo_number_display
                                    || r.work_order_number
                                    || r.linked_wo_number;
                  return (
                  <tr key={r.id}
                      data-testid={`mp-row-${r.id}`}
                      data-unlinked={unlinked ? 'true' : 'false'}
                      style={unlinked ? {
                        background: 'hsl(48 96% 96%)',
                        boxShadow: 'inset 3px 0 0 hsl(48 90% 55%)',
                      } : undefined}>
                    <td className="font-medium">
                      <div className="flex items-center gap-1.5">
                        <Link
                          to={`/projects/${r.id}`}
                          data-testid={`mp-open-detail-${r.id}`}
                          className="hover:underline text-[hsl(var(--primary))]"
                        >
                          {r.project_name}
                        </Link>
                        {unlinked && (
                          <span
                            data-testid={`mp-unlinked-badge-${r.id}`}
                            title="Internal / speculative project — not linked to a Work Order"
                            className="text-[9.5px] font-semibold px-1.5 py-[1px] rounded-full uppercase tracking-wide"
                            style={{ background: 'hsl(48 90% 55%)', color: '#3a2900' }}
                          >
                            Unlinked
                          </span>
                        )}
                      </div>
                    </td>
                    <td>{r.client}</td>
                    <td>
                      <SopStagePopover stage={r.project_stage} rowId={r.id} />
                    </td>
                    <td><span className={`chip ${STATUS_CHIP[r.status] || ''}`}>{STATUS_LABEL[r.status] || r.status}</span></td>
                    <td className="font-mono text-[12px]" data-testid={`mp-wo-number-${r.id}`}>
                      {displayNo || <span className="text-[hsl(38_50%_45%)] italic font-sans not-italic text-[11.5px]">No WO</span>}
                    </td>
                    <td className="tabular-nums">{r.tenure_months ? `${r.tenure_months} mo` : '—'}</td>
                    <td><MaskedAmount amount={r.total_amount} masked={r.amounts_masked} /></td>
                    <td><MaskedAmount amount={r.paid_amount} masked={r.amounts_masked} /></td>
                    <td><MaskedAmount amount={r.unpaid_amount} masked={r.amounts_masked} /></td>
                    <td className="text-[12px]">
                      <MilestoneSliderPopover project={r} canAct={canEdit} onChanged={load} />
                    </td>
                    <td className="text-[12px] text-[hsl(var(--foreground-muted))] max-w-[180px]">
                      <LeadCell row={r} usersById={usersById} />
                    </td>
                    {canEdit && (
                      <td>
                        <div className="flex items-center gap-1 justify-end">
                          {/* Attach WO — only Owner + DMs can promote an unlinked project. */}
                          {unlinked && user.role !== 'operations' && (
                            <button
                              className="p-1 text-[hsl(38_60%_30%)]"
                              onClick={() => setAttachTarget(r)}
                              data-testid={`mp-attach-wo-${r.id}`}
                              title="Attach a Work Order — promote this into a billable project"
                            >
                              <LinkIcon size={13} />
                            </button>
                          )}
                          {/* Ops can only edit unlinked rows; hide the pencil on WO-linked rows for Ops */}
                          {(user.role !== 'operations' || unlinked) && (
                            <button className="p-1" onClick={() => setOpenForm({ new: false, row: r })} data-testid={`mp-edit-${r.id}`}>
                              <Pencil size={13} />
                            </button>
                          )}
                          {user.role === 'owner' && (
                            <button className="p-1 text-[hsl(var(--danger))]" onClick={() => setOpenDelete(r)} data-testid={`mp-delete-${r.id}`}>
                              <Trash2 size={13} />
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {openForm && (
        <ProjectFormDialog
          state={openForm}
          onClose={() => setOpenForm(null)}
          onSaved={() => { setOpenForm(null); load(); }}
          stages={stages}
          statuses={statuses}
          users={users}
        />
      )}

      <Dialog open={!!openDelete} onOpenChange={() => setOpenDelete(null)}>
        <DialogContent data-testid="mp-delete-dialog">
          <DialogHeader>
            <DialogTitle>Delete “{openDelete?.project_name}”?</DialogTitle>
            <DialogDescription>
              This removes the project row from the app. The linked Google Sheet is not touched.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpenDelete(null)}>Cancel</Button>
            <Button variant="destructive" onClick={onDelete} data-testid="mp-delete-confirm">Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ImportDialog
        open={openImport}
        onClose={() => setOpenImport(false)}
        onCommitted={() => { setOpenImport(false); load(); }}
      />

      {attachTarget && (
        <AttachWorkOrderDialog
          project={attachTarget}
          open={!!attachTarget}
          onClose={() => setAttachTarget(null)}
          onDone={() => { setAttachTarget(null); load(); }}
        />
      )}

      <WoSyncReviewDialog
        open={openWoSync}
        onOpenChange={setOpenWoSync}
        onApplied={load}
      />
    </AppShell>
  );
}

const MP_STAGE_LABEL = {
  advance_retainer: 'Advance / Retainer',
  strategy_concept: 'Strategy & Concept', schematic: 'Schematic', dd: 'Design Development',
  tdp: 'TDP', construction_admin_closure: 'Construction Admin & Closure',
};

const MPVisualSummary = ({ rows }) => {
  if (!rows || rows.length === 0) {
    return (
      <div className="card p-4" data-testid="mp-visual-summary-empty">
        <div className="text-[12.5px] text-[hsl(var(--foreground-muted))] italic">
          You&rsquo;re not linked to any master projects yet — they appear here once a lead adds you.
        </div>
      </div>
    );
  }
  const byStatus = { active: 0, on_hold: 0, closed: 0 };
  const byStage = {};
  rows.forEach(r => {
    byStatus[r.status] = (byStatus[r.status] || 0) + 1;
    if (r.status !== 'closed') byStage[r.project_stage] = (byStage[r.project_stage] || 0) + 1;
  });
  const stageItems = Object.keys(MP_STAGE_LABEL)
    .map(k => ({ label: MP_STAGE_LABEL[k], count: byStage[k] || 0 }))
    .filter(i => i.count > 0);
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="mp-visual-summary">
      <div className="card p-4 elev-1">
        <div className="text-[11px] uppercase tracking-wider text-[hsl(var(--foreground-subtle))] font-semibold mb-2">
          Portfolio status
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="chip chip-success tabular-nums" data-testid="mp-kpi-active">{byStatus.active || 0} active</span>
          <span className={`chip ${byStatus.on_hold ? 'chip-warning' : ''} tabular-nums`} data-testid="mp-kpi-on-hold">{byStatus.on_hold || 0} on hold</span>
          <span className="chip tabular-nums" data-testid="mp-kpi-closed">{byStatus.closed || 0} closed</span>
        </div>
        <div className="h-2 rounded-full flex overflow-hidden mt-3" style={{ background: 'hsl(var(--surface-2))' }}>
          {[['active', 'hsl(var(--success))'], ['on_hold', 'hsl(var(--warning))'], ['closed', 'hsl(var(--neutral))']].map(([k, c]) => (
            (byStatus[k] || 0) > 0 && (
              <div key={k} style={{ width: `${(byStatus[k] / rows.length) * 100}%`, background: c }} />
            )
          ))}
        </div>
      </div>
      <div className="card p-4 elev-1">
        <div className="text-[11px] uppercase tracking-wider text-[hsl(var(--foreground-subtle))] font-semibold mb-2">
          Active projects by stage
        </div>
        <MiniBars items={stageItems} testId="mp-stage-bars" />
      </div>
    </div>
  );
};

const LeadCell = ({ row, usersById }) => {
  const cl = row.creative_lead_id ? usersById[row.creative_lead_id]?.name : null;
  const tl = row.technical_lead_id ? usersById[row.technical_lead_id]?.name : null;
  const team = (row.team_member_ids || []).map(id => usersById[id]?.name).filter(Boolean);
  return (
    <div className="space-y-0.5">
      {cl && <div>Creative: <span className="text-[hsl(var(--foreground))]">{cl}</span></div>}
      {tl && <div>Technical: <span className="text-[hsl(var(--foreground))]">{tl}</span></div>}
      {team.length > 0 && <div className="truncate">Team: {team.join(', ')}</div>}
      {!cl && !tl && team.length === 0 && <div className="italic text-[hsl(var(--foreground-subtle))]">Unassigned</div>}
    </div>
  );
};

const ProjectFormDialog = ({ state, onClose, onSaved, stages, statuses, users }) => {
  const { user } = useAuth();
  const [row, setRow] = useState(state.row);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setRow(prev => ({ ...prev, [k]: v }));
  // Design Ops may only create/edit UNLINKED projects (no Work Order attached).
  // Disable the two WO fields so the constraint is visible in the UI.
  const opsRestricted = user?.role === 'operations';
  const addMilestone = () => setRow(p => ({ ...p, milestones: [...(p.milestones || []), { name: '', status: 'pending', target_date: '', amount: '' }] }));
  const removeMilestone = (i) => setRow(p => ({ ...p, milestones: p.milestones.filter((_, j) => j !== i) }));
  const setMilestone = (i, k, v) => setRow(p => ({
    ...p,
    milestones: p.milestones.map((m, j) => j === i ? { ...m, [k]: v } : m),
  }));

  const submit = async () => {
    if (!row.project_name?.trim() || !row.client?.trim()) {
      toast.error('Project name and client are required'); return;
    }
    setSaving(true);
    try {
      const payload = {
        ...row,
        // Iter39 — WO assignment is now id-based via attach-work-order.
        // Strip both free-text WO fields from the form payload so form
        // edits never accidentally overwrite a linked WO or fake a link.
        work_order_number: undefined,
        work_order_date: undefined,
        tenure_months: row.tenure_months === '' ? null : Number(row.tenure_months),
        total_amount: row.total_amount === '' ? null : Number(row.total_amount),
        paid_amount: row.paid_amount === '' ? null : Number(row.paid_amount),
        unpaid_amount: row.unpaid_amount === '' ? null : Number(row.unpaid_amount),
        creative_lead_id: row.creative_lead_id || null,
        technical_lead_id: row.technical_lead_id || null,
        milestones: (row.milestones || []).filter(m => m.name?.trim()).map(m => ({
          name: m.name, status: m.status || 'pending',
          target_date: m.target_date || null,
          amount: m.amount === '' || m.amount == null ? null : Number(m.amount),
          owner_id: m.owner_id || null,
        })),
      };
      if (state.new) {
        await api.post('/master-projects', payload);
        toast.success('Project created');
      } else {
        await api.patch(`/master-projects/${row.id}`, payload);
        toast.success('Project updated');
      }
      onSaved();
    } catch (e) { toast.error(parseErr(e)); }
    finally { setSaving(false); }
  };

  const leadOptions = users.filter(u => ['dm_creative', 'dm_technical', 'dm_architecture', 'owner'].includes(u.role));
  const memberOptions = users.filter(u => u.status === 'active');

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-[720px] max-h-[90vh] overflow-y-auto" data-testid="mp-form-dialog">
        <DialogHeader>
          <DialogTitle>{state.new ? 'New project' : `Edit “${row.project_name}”`}</DialogTitle>
          <DialogDescription>
            {state.new ? 'Add a project to the master list.' : 'Changes are audited and (once sync is live) will push to Google Sheets.'}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {opsRestricted && (
            <div
              data-testid="mp-form-ops-note"
              className="p-2.5 rounded-md text-[12.5px] border"
              style={{
                background: 'hsl(48 96% 96%)',
                borderColor: 'hsl(48 90% 75%)',
                color: 'hsl(38 60% 25%)',
              }}
            >
              You&rsquo;re creating an <strong>unlinked</strong> project — internal or speculative work that isn&rsquo;t tied to a paid Work Order. Owner or a Design Manager can attach a WO later if it turns billable. Unlinked projects appear in yellow across the app for easy tracking.
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <Field label="Project name *">
              <Input value={row.project_name || ''} onChange={e => set('project_name', e.target.value)} data-testid="mp-form-name" />
            </Field>
            <Field label="Client *">
              <Input value={row.client || ''} onChange={e => set('client', e.target.value)} data-testid="mp-form-client" />
            </Field>
            <Field label="Work Order (assign later)">
              <div className="border rounded-md h-9 text-[12.5px] px-2 flex items-center bg-[hsl(var(--surface-1))]"
                   style={{ borderColor: 'hsl(var(--border))' }}
                   data-testid="mp-form-wo-hint">
                {state.new
                  ? <span className="text-[hsl(var(--foreground-muted))]">Create project first, then attach a Work Order from the row</span>
                  : row.work_order_id
                    ? <span className="font-mono">{row.work_order_number} <span className="text-[hsl(var(--success))] ml-1">· Linked</span></span>
                    : row.work_order_number
                      ? <span className="font-mono">{row.work_order_number} <span className="text-[hsl(38_60%_45%)] ml-1">· Typed only — needs Owner review</span></span>
                      : <span className="text-[hsl(var(--foreground-muted))]">No Work Order attached</span>}
              </div>
            </Field>
            <Field label="Work Order date">
              <div className="border rounded-md h-9 text-[12.5px] px-2 flex items-center bg-[hsl(var(--surface-1))]"
                   style={{ borderColor: 'hsl(var(--border))' }}
                   data-testid="mp-form-wo-date-hint">
                {row.work_order_date || <span className="text-[hsl(var(--foreground-muted))]">Comes from the linked WO</span>}
              </div>
            </Field>
            <Field label="Stage">
              <select className="border rounded-md h-9 text-[13px] px-2 w-full bg-[hsl(var(--card))]"
                      style={{ borderColor: 'hsl(var(--border))' }}
                      value={row.project_stage || ''}
                      onChange={e => set('project_stage', e.target.value)}
                      data-testid="mp-form-stage">
                {stages.map(s => <option key={s} value={s}>{STAGE_LABEL[s] || s}</option>)}
              </select>
            </Field>
            <Field label="Status">
              <select className="border rounded-md h-9 text-[13px] px-2 w-full bg-[hsl(var(--card))]"
                      style={{ borderColor: 'hsl(var(--border))' }}
                      value={row.status || 'active'}
                      onChange={e => set('status', e.target.value)}
                      data-testid="mp-form-status">
                {statuses.map(s => <option key={s} value={s}>{STATUS_LABEL[s] || s}</option>)}
              </select>
            </Field>
            <Field label="Tenure (months)">
              <Input type="number" min="0" value={row.tenure_months ?? ''} onChange={e => set('tenure_months', e.target.value)} data-testid="mp-form-tenure" />
            </Field>
            <Field label="Total amount (₹)">
              <Input type="number" min="0" value={row.total_amount ?? ''} onChange={e => set('total_amount', e.target.value)} data-testid="mp-form-total" />
            </Field>
            <Field label="Paid amount (₹)">
              <Input type="number" min="0" value={row.paid_amount ?? ''} onChange={e => set('paid_amount', e.target.value)} data-testid="mp-form-paid" />
            </Field>
            <Field label="Unpaid amount (₹)">
              <Input type="number" min="0" value={row.unpaid_amount ?? ''} onChange={e => set('unpaid_amount', e.target.value)} data-testid="mp-form-unpaid" />
            </Field>
            <Field label="Creative Lead">
              <select className="border rounded-md h-9 text-[13px] px-2 w-full bg-[hsl(var(--card))]"
                      style={{ borderColor: 'hsl(var(--border))' }}
                      value={row.creative_lead_id || ''}
                      onChange={e => set('creative_lead_id', e.target.value)}
                      data-testid="mp-form-creative-lead">
                <option value="">— None —</option>
                {leadOptions.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
            </Field>
            <Field label="Technical Design Lead">
              <select className="border rounded-md h-9 text-[13px] px-2 w-full bg-[hsl(var(--card))]"
                      style={{ borderColor: 'hsl(var(--border))' }}
                      value={row.technical_lead_id || ''}
                      onChange={e => set('technical_lead_id', e.target.value)}
                      data-testid="mp-form-technical-lead">
                <option value="">— None —</option>
                {leadOptions.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
            </Field>
          </div>

          <Field label="Team members assigned">
            <MultiUserSelect
              value={row.team_member_ids || []}
              onChange={ids => set('team_member_ids', ids)}
              options={memberOptions}
            />
          </Field>

          <div>
            <div className="flex items-center justify-between">
              <label className="text-[12px] font-medium">Milestones</label>
              <Button size="sm" variant="outline" onClick={addMilestone} data-testid="mp-form-add-milestone">
                <Plus size={12} className="mr-1" />Add milestone
              </Button>
            </div>
            <div className="mt-2 space-y-2">
              {(row.milestones || []).length === 0 && (
                <div className="text-[12px] text-[hsl(var(--foreground-subtle))] italic border rounded p-3"
                     style={{ borderColor: 'hsl(var(--border))' }}>
                  No milestones. Add them to mirror the master sheet.
                </div>
              )}
              {(row.milestones || []).map((m, i) => (
                <div key={i} className="grid grid-cols-[2fr_1fr_1fr_1fr_1.2fr_auto] gap-2 items-center">
                  <Input value={m.name} onChange={e => setMilestone(i, 'name', e.target.value)} placeholder="Milestone name" data-testid={`mp-form-mile-name-${i}`} />
                  <select className="border rounded-md h-9 text-[12.5px] px-2 bg-[hsl(var(--card))]" style={{ borderColor: 'hsl(var(--border))' }}
                          value={m.status || 'pending'} onChange={e => setMilestone(i, 'status', e.target.value)}
                          data-testid={`mp-form-mile-status-${i}`}>
                    <option value="pending">Pending</option>
                    <option value="in_progress">In progress</option>
                    <option value="done">Done</option>
                    <option value="invoiced">Invoiced</option>
                    <option value="paid">Paid</option>
                  </select>
                  <Input type="date" value={m.target_date || ''} onChange={e => setMilestone(i, 'target_date', e.target.value)} data-testid={`mp-form-mile-date-${i}`} />
                  <Input type="number" value={m.amount ?? ''} onChange={e => setMilestone(i, 'amount', e.target.value)} placeholder="Amount ₹" data-testid={`mp-form-mile-amount-${i}`} />
                  <select className="border rounded-md h-9 text-[12.5px] px-2 bg-[hsl(var(--card))]" style={{ borderColor: 'hsl(var(--border))' }}
                          value={m.owner_id || ''} onChange={e => setMilestone(i, 'owner_id', e.target.value || null)}
                          data-testid={`mp-form-mile-owner-${i}`}>
                    <option value="">— Owner —</option>
                    {memberOptions.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                  </select>
                  <button className="p-1 text-[hsl(var(--danger))]" onClick={() => removeMilestone(i)} data-testid={`mp-form-mile-remove-${i}`}>
                    <XCircle size={14} />
                  </button>
                </div>
              ))}
            </div>
          </div>

          <Field label="Notes">
            <Textarea rows={2} value={row.notes || ''} onChange={e => set('notes', e.target.value)} data-testid="mp-form-notes" />
          </Field>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="mp-form-save">
            {saving ? 'Saving…' : (state.new ? 'Create' : 'Save changes')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const Field = ({ label, children }) => (
  <div>
    <label className="text-[12px] font-medium block mb-1">{label}</label>
    {children}
  </div>
);

const MultiUserSelect = ({ value, onChange, options }) => {
  const toggle = (id) => {
    if (value.includes(id)) onChange(value.filter(x => x !== id));
    else onChange([...value, id]);
  };
  return (
    <div className="border rounded-md p-2 max-h-[160px] overflow-y-auto grid grid-cols-2 gap-1"
         style={{ borderColor: 'hsl(var(--border))' }}
         data-testid="mp-form-team">
      {options.map(u => (
        <label key={u.id} className="flex items-center gap-2 text-[12.5px] px-1 py-0.5 cursor-pointer hover:bg-[hsl(var(--surface-2))] rounded">
          <input type="checkbox" checked={value.includes(u.id)} onChange={() => toggle(u.id)}
                 data-testid={`mp-form-team-${u.id}`} />
          <span className="truncate">{u.name}</span>
        </label>
      ))}
    </div>
  );
};


const STAGE_LABEL_LOCAL = STAGE_LABEL;

const ImportDialog = ({ open, onClose, onCommitted }) => {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [invalidOnly, setInvalidOnly] = useState(false);

  useEffect(() => {
    if (!open) { setFile(null); setPreview(null); setInvalidOnly(false); }
  }, [open]);

  const onFile = (f) => {
    setFile(f);
    setPreview(null);
  };

  const submitPreview = async () => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await api.post('/master-projects/import/preview', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setPreview(r.data);
    } catch (e) { toast.error(parseErr(e)); }
    finally { setUploading(false); }
  };

  const commit = async () => {
    if (!preview) return;
    const rowsToCommit = preview.rows.filter(r => r.errors.length === 0).map(r => r.data);
    if (rowsToCommit.length === 0) {
      toast.error('Nothing to import — fix errors and re-upload.');
      return;
    }
    setCommitting(true);
    try {
      const r = await api.post('/master-projects/import/commit', { rows: rowsToCommit });
      toast.success(`Imported ${r.data.inserted} project(s)`);
      onCommitted();
    } catch (e) { toast.error(parseErr(e)); }
    finally { setCommitting(false); }
  };

  const shownRows = preview
    ? (invalidOnly ? preview.rows.filter(r => r.errors.length > 0) : preview.rows)
    : [];

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-[960px] max-h-[90vh] overflow-y-auto" data-testid="mp-import-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet size={18} />Import from Excel
          </DialogTitle>
          <DialogDescription>
            Upload the filled-in Master Projects template. We&apos;ll validate every row and
            show any errors before anything is written to the app.
          </DialogDescription>
        </DialogHeader>

        {!preview && (
          <div className="space-y-3">
            <div className="border-2 border-dashed rounded-lg p-6 text-center"
                 style={{ borderColor: 'hsl(var(--border))' }}>
              <FileSpreadsheet size={30} className="mx-auto text-[hsl(var(--foreground-muted))]" />
              <p className="text-[13px] mt-2">
                {file ? file.name : 'Choose an .xlsx file to import'}
              </p>
              <input type="file" accept=".xlsx"
                     onChange={e => onFile(e.target.files?.[0])}
                     className="mt-3 mx-auto block text-[12px]"
                     data-testid="mp-import-file" />
              <p className="text-[11.5px] text-[hsl(var(--foreground-subtle))] mt-3">
                Use the downloaded template to guarantee the column order.
                Max 5 MB.
              </p>
            </div>
          </div>
        )}

        {preview && (
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              <span className="chip chip-info" data-testid="mp-import-total">
                {preview.total} row{preview.total === 1 ? '' : 's'}
              </span>
              <span className="chip chip-success" data-testid="mp-import-valid">
                <CheckCircle2 size={11} className="inline mr-1" />{preview.valid_count} valid
              </span>
              {preview.invalid_count > 0 ? (
                <span className="chip chip-danger" data-testid="mp-import-invalid">
                  <AlertTriangle size={11} className="inline mr-1" />{preview.invalid_count} with errors
                </span>
              ) : (
                <span className="chip">All rows are valid</span>
              )}
              {preview.invalid_count > 0 && (
                <label className="ml-auto flex items-center gap-2 text-[12px] cursor-pointer">
                  <input type="checkbox" checked={invalidOnly} onChange={e => setInvalidOnly(e.target.checked)}
                         data-testid="mp-import-invalid-only" />
                  Show only rows with errors
                </label>
              )}
            </div>

            {preview.invalid_count > 0 && (
              <div className="border rounded-md p-2.5 text-[12px] flex items-start gap-2"
                   style={{ borderColor: 'hsl(var(--danger)/.5)', background: 'hsl(var(--danger)/.06)' }}>
                <AlertTriangle size={13} className="mt-0.5 flex-none text-[hsl(var(--danger))]" />
                <div>
                  Fix the flagged columns in your file and upload it again. Only valid rows will be imported when you click <b>Import valid rows</b>.
                </div>
              </div>
            )}

            <div className="border rounded-md overflow-x-auto max-h-[420px]"
                 style={{ borderColor: 'hsl(var(--border))' }}>
              <table className="tbl text-[12.5px]" data-testid="mp-import-preview-table">
                <thead>
                  <tr>
                    <th style={{ width: 40 }}>#</th>
                    <th>Project</th>
                    <th>Client</th>
                    <th>Stage</th>
                    <th>WO</th>
                    <th>Tenure</th>
                    <th>Total</th>
                    <th>Milestones</th>
                    <th>Team</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {shownRows.map(r => (
                    <tr key={r.row_index}
                        data-testid={`mp-import-row-${r.row_index}`}
                        className={r.errors.length ? 'bg-[hsl(var(--danger)/.05)]' : ''}>
                      <td className="tabular-nums text-[hsl(var(--foreground-subtle))]">{r.row_index}</td>
                      <td className="font-medium">{r.data.project_name || <em className="text-[hsl(var(--danger))]">missing</em>}</td>
                      <td>{r.data.client || <em className="text-[hsl(var(--danger))]">missing</em>}</td>
                      <td>{r.data.project_stage ? (STAGE_LABEL_LOCAL[r.data.project_stage] || r.data.project_stage) : <em className="text-[hsl(var(--danger))]">missing</em>}</td>
                      <td className="font-mono text-[11.5px]">{r.data.work_order_number || '—'}</td>
                      <td>
                        {r.data.tenure_start_date || '—'}
                        {r.data.tenure_end_date && <>→ {r.data.tenure_end_date}</>}
                        {r.data.tenure_months != null && <span className="text-[11px] text-[hsl(var(--foreground-muted))]"> ({r.data.tenure_months} mo)</span>}
                      </td>
                      <td className="tabular-nums">{r.data.total_amount != null ? new Intl.NumberFormat('en-IN').format(r.data.total_amount) : '—'}</td>
                      <td>{(r.data.milestones || []).length}</td>
                      <td>{(r.data.team_member_ids || []).length}</td>
                      <td>
                        {r.errors.length === 0 ? (
                          <span className="chip chip-success" data-testid={`mp-import-ok-${r.row_index}`}>
                            <CheckCircle2 size={10} className="inline mr-1" />OK
                          </span>
                        ) : (
                          <div className="space-y-0.5">
                            {r.errors.map((err, i) => (
                              <div key={i} className="text-[11px] text-[hsl(var(--danger))]"
                                   data-testid={`mp-import-err-${r.row_index}-${i}`}>
                                <b>{err.column}:</b> {err.message}
                              </div>
                            ))}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                  {shownRows.length === 0 && (
                    <tr><td colSpan={10} className="text-center text-[hsl(var(--foreground-muted))] py-6">
                      {invalidOnly ? 'No rows with errors — you can import.' : 'No rows to show.'}
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <DialogFooter className="justify-between">
          {preview ? (
            <Button variant="outline" onClick={() => { setPreview(null); setFile(null); }}
                    data-testid="mp-import-restart">
              Upload a different file
            </Button>
          ) : <div />}
          <div className="flex gap-2">
            <Button variant="ghost" onClick={onClose}>Cancel</Button>
            {!preview ? (
              <Button onClick={submitPreview} disabled={!file || uploading}
                      data-testid="mp-import-preview">
                {uploading ? 'Reading…' : 'Preview'}
              </Button>
            ) : (
              <Button onClick={commit} disabled={committing || preview.valid_count === 0}
                      data-testid="mp-import-commit">
                {committing ? 'Importing…' : `Import ${preview.valid_count} valid row${preview.valid_count === 1 ? '' : 's'}`}
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};


/** Stage cell with an inline SOP popover. Renders the stage chip as usual,
 *  plus a small "SOPs" ghost-button that opens a popover of contextual SOP chips.
 *  Keeps the table row clean while making SOPs one click away. */
const SopStagePopover = ({ stage, rowId }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="inline-flex items-center gap-1">
      <span className="chip chip-info">{STAGE_LABEL[stage] || stage}</span>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            data-testid={`mp-sop-trigger-${rowId}`}
            title="Related SOPs for this stage"
            className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[11px] text-[hsl(var(--foreground-muted))]
                       hover:bg-[hsl(var(--surface-2))] hover:text-[hsl(var(--primary))] transition-colors"
          >
            <FileSearch size={11} />
            <ChevronDown size={10} />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-[420px] p-3" data-testid={`mp-sop-popover-${rowId}`} align="start">
          <div className="text-[11px] uppercase tracking-wider text-[hsl(var(--foreground-subtle))] font-semibold mb-2">
            Related SOPs · {STAGE_LABEL[stage] || stage}
          </div>
          <StageSOPChips stage={stage} testId={`mp-sop-${rowId}`} />
        </PopoverContent>
      </Popover>
    </div>
  );
};
